<script setup>
import { computed, nextTick, onBeforeUnmount, ref } from 'vue';
import { gsap } from 'gsap';
import { encontrarElementoVisivel } from '@/components/elementos/TourGuiado/elementoAlvo';

const ESPACAMENTO = 14;
const MARGEM_TELA = 12;
const LARGURA_BALAO = 340;
const TEMPO_AUSENCIA = 400;
const OPACIDADE_FUNDO = 0.6;
const PADDING_DESTAQUE = 8;
const FAIXA_CONFORTAVEL = 0.28;

const props = defineProps({
    passos: {
        type: Array,
        default: () => [],
    },
    // Controlado: quem decide o passo é o pai (tutoriais entre telas). Os botões só emitem
    // `proximo`/`anterior`, nada é pulado sozinho e o destaque não bloqueia a tela
    controlado: {
        type: Boolean,
        default: false,
    },
    rotulos: {
        type: Object,
        default: () => ({
            anterior: 'Anterior',
            proximo: 'Próximo',
            finalizar: 'Finalizar',
        }),
    },
});

const emit = defineEmits(['iniciar', 'finalizar', 'pular', 'mudar-passo', 'proximo', 'anterior', 'alvo-ausente']);

const ativo = ref(false);
const oculto = ref(false);
const indice = ref(0);
const lado = ref('bottom');

const destaqueEl = ref(null);
const pulsoEl = ref(null);
const balaoEl = ref(null);
const cartaoEl = ref(null);
const conteudoEl = ref(null);
const etapasEl = ref(null);

const passoAtual = computed(() => props.passos[indice.value] ?? null);
const primeiroPasso = computed(() => indice.value === 0);
const ultimoPasso = computed(() => indice.value === props.passos.length - 1);
const rotuloPasso = computed(() => `Passo ${String(indice.value + 1).padStart(2, '0')}`);
const titulo = computed(() => passoAtual.value?.header?.title ?? '');
const rotuloAcao = computed(() => (ultimoPasso.value ? props.rotulos.finalizar : props.rotulos.proximo));
const iconeAcao = computed(() => (ultimoPasso.value ? 'pi pi-check' : 'pi pi-arrow-right'));
const semAlvo = computed(() => !passoAtual.value?.target);

const idTitulo = `tour-guiado-titulo-${Math.random().toString(36).slice(2, 9)}`;

// No modo controlado, o pai decide o que vem depois do último passo (ex.: voltar ao roteiro principal)
const avancar = () => (ultimoPasso.value && !props.controlado ? finalizar() : proximo());

const movimentoReduzido = () =>
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

const dur = (segundos) => (movimentoReduzido() ? 0 : segundos);

/** Setters do GSAP (quickTo) que suavizam cada propriedade rumo ao valor alvo. */
let seguidor = null;
let animacaoPulso = null;
let animacaoFundo = null;
let rodandoLoop = false;
let tamanhoBalao = { largura: LARGURA_BALAO, altura: 160 };
let focoAnterior = null;
let ausenteDesde = null;
let ausenciaAvisada = false;

const elementoAlvo = (passo) => encontrarElementoVisivel(passo?.target);

// Passo sem `target` é um balão centralizado (abertura/encerramento), sempre disponível
const passoDisponivel = (passo) => !passo?.target || Boolean(elementoAlvo(passo));

const definirIndice = (novoIndice) => {
    indice.value = novoIndice;
    ausenteDesde = null;
    ausenciaAvisada = false;
};

const campoEditavel = (elemento) =>
    Boolean(elemento?.closest?.('input, textarea, select, [contenteditable="true"]'));

/**
 * Calcula onde o recorte de destaque e o balão devem ficar para o passo atual.
 * Faz o "flip" do posicionamento quando falta espaço na direção pedida e
 * mantém o balão sempre dentro da área visível.
 */
const calcularLayout = () => {
    const passo = passoAtual.value;
    if (passo && !passo.target) return layoutCentralizado();

    const alvo = elementoAlvo(passo);
    if (!alvo) return null;

    const rect = alvo.getBoundingClientRect();
    const destaque = {
        x: rect.left - PADDING_DESTAQUE,
        y: rect.top - PADDING_DESTAQUE,
        largura: rect.width + PADDING_DESTAQUE * 2,
        altura: rect.height + PADDING_DESTAQUE * 2,
    };

    const { largura, altura } = tamanhoBalao;

    let posicao = passo?.params?.placement ?? 'bottom';
    const espacoAbaixo = window.innerHeight - rect.bottom;
    const espacoAcima = rect.top;

    // Sem largura para o balão ao lado (ex.: mobile), ele vai para cima ou para baixo do alvo
    const semEspacoLateral = (posicao === 'left' && rect.left < largura + ESPACAMENTO)
        || (posicao === 'right' && window.innerWidth - rect.right < largura + ESPACAMENTO);
    if (semEspacoLateral) posicao = espacoAbaixo > espacoAcima ? 'bottom' : 'top';

    if (posicao === 'bottom' && espacoAbaixo < altura + ESPACAMENTO && espacoAcima > espacoAbaixo) {
        posicao = 'top';
    } else if (posicao === 'top' && espacoAcima < altura + ESPACAMENTO && espacoAbaixo > espacoAcima) {
        posicao = 'bottom';
    }

    let x;
    let y;

    if (posicao === 'top') {
        y = rect.top - altura - ESPACAMENTO;
        x = rect.left + rect.width / 2 - largura / 2;
    } else if (posicao === 'left') {
        y = rect.top + rect.height / 2 - altura / 2;
        x = rect.left - largura - ESPACAMENTO;
    } else if (posicao === 'right') {
        y = rect.top + rect.height / 2 - altura / 2;
        x = rect.right + ESPACAMENTO;
    } else {
        posicao = 'bottom';
        y = rect.bottom + ESPACAMENTO;
        x = rect.left + rect.width / 2 - largura / 2;
    }

    x = Math.min(Math.max(x, MARGEM_TELA), Math.max(MARGEM_TELA, window.innerWidth - largura - MARGEM_TELA));
    y = Math.min(Math.max(y, MARGEM_TELA), Math.max(MARGEM_TELA, window.innerHeight - altura - MARGEM_TELA));

    const vertical = posicao === 'left' || posicao === 'right';
    const centro = vertical ? rect.top + rect.height / 2 - y : rect.left + rect.width / 2 - x;
    const limite = vertical ? altura : largura;

    return {
        destaque,
        balao: { x, y },
        posicao,
        seta: Math.min(Math.max(centro, 20), limite - 20),
    };
};

/** Recorte de tamanho zero no centro: o box-shadow escurece a tela inteira e o balão fica no meio. */
const layoutCentralizado = () => {
    const { largura, altura } = tamanhoBalao;

    return {
        destaque: { x: window.innerWidth / 2, y: window.innerHeight / 2, largura: 0, altura: 0 },
        balao: {
            x: Math.max(MARGEM_TELA, (window.innerWidth - largura) / 2),
            y: Math.max(MARGEM_TELA, (window.innerHeight - altura) / 2),
        },
        posicao: 'centro',
        seta: 0,
    };
};

/** No modo controlado, avisa o pai uma vez quando o alvo do passo some (ex.: diálogo fechado). */
const verificarAusencia = (layout) => {
    if (!props.controlado || oculto.value || layout) {
        ausenteDesde = null;
        return;
    }
    if (ausenciaAvisada) return;

    ausenteDesde ??= performance.now();
    if (performance.now() - ausenteDesde < TEMPO_AUSENCIA) return;

    ausenciaAvisada = true;
    emit('alvo-ausente', indice.value);
};

const medirBalao = () => {
    if (!balaoEl.value) return;
    tamanhoBalao = {
        largura: balaoEl.value.offsetWidth || LARGURA_BALAO,
        altura: balaoEl.value.offsetHeight || 160,
    };
};

const criarSeguidor = () => {
    const config = { duration: 0.4, ease: 'power3' };
    seguidor = {
        x: gsap.quickTo(destaqueEl.value, 'x', config),
        y: gsap.quickTo(destaqueEl.value, 'y', config),
        largura: gsap.quickTo(destaqueEl.value, 'width', config),
        altura: gsap.quickTo(destaqueEl.value, 'height', config),
        balaoX: gsap.quickTo(balaoEl.value, 'x', config),
        balaoY: gsap.quickTo(balaoEl.value, 'y', config),
    };
};

/**
 * Único ponto que escreve posição na tela. Roda a cada quadro enquanto o tour
 * está ativo, então rolagem, resize e troca de passo usam o mesmo caminho —
 * o `quickTo` interpola sozinho e nada disputa a mesma propriedade.
 */
const acompanhar = () => {
    const layout = calcularLayout();
    verificarAusencia(layout);
    if (!layout || !seguidor) return;

    lado.value = layout.posicao;
    cartaoEl.value?.style.setProperty('--seta-offset', `${layout.seta}px`);

    seguidor.x(layout.destaque.x);
    seguidor.y(layout.destaque.y);
    seguidor.largura(layout.destaque.largura);
    seguidor.altura(layout.destaque.altura);
    seguidor.balaoX(layout.balao.x);
    seguidor.balaoY(layout.balao.y);
};

/** Posiciona sem interpolação — usado no primeiro passo, para não vir "voando" da origem. */
const posicionarInstantaneo = () => {
    const layout = calcularLayout();
    if (!layout) return;

    lado.value = layout.posicao;
    cartaoEl.value?.style.setProperty('--seta-offset', `${layout.seta}px`);

    // Ao reaparecer em outra tela, os quickTo antigos ainda podem estar a caminho da posição velha
    gsap.killTweensOf([destaqueEl.value, balaoEl.value]);
    gsap.set(destaqueEl.value, {
        x: layout.destaque.x,
        y: layout.destaque.y,
        width: layout.destaque.largura,
        height: layout.destaque.altura,
    });
    gsap.set(balaoEl.value, { x: layout.balao.x, y: layout.balao.y });
    criarSeguidor();
};

const iniciarLoop = () => {
    if (rodandoLoop) return;
    gsap.ticker.add(acompanhar);
    rodandoLoop = true;
};

const pararLoop = () => {
    if (!rodandoLoop) return;
    gsap.ticker.remove(acompanhar);
    rodandoLoop = false;
};

/**
 * Rola até o alvo quando ele não está numa faixa confortável da tela.
 * O `enableScrolling` dos passos é ignorado de propósito: sem rolagem o tour
 * apontava para campos fora da área visível.
 */
const garantirVisivel = (alvo) => {
    const rect = alvo.getBoundingClientRect();
    const limiteSuperior = window.innerHeight * FAIXA_CONFORTAVEL;
    const limiteInferior = window.innerHeight * (1 - FAIXA_CONFORTAVEL);
    const cabeNaFaixa = rect.height < limiteInferior - limiteSuperior;

    const foraDaFaixa = cabeNaFaixa
        ? rect.top < limiteSuperior || rect.bottom > limiteInferior
        : rect.top < MARGEM_TELA || rect.top > limiteInferior;

    if (!foraDaFaixa) return;

    alvo.scrollIntoView({
        behavior: movimentoReduzido() ? 'auto' : 'smooth',
        block: 'center',
        inline: 'nearest',
    });
};

const iniciarPulso = () => {
    animacaoPulso?.kill();
    if (!pulsoEl.value || movimentoReduzido()) return;
    animacaoPulso = gsap.fromTo(
        pulsoEl.value,
        { scale: 1, opacity: 0.7 },
        { scale: 1.05, opacity: 0, duration: 1.8, ease: 'power2.out', repeat: -1, transformOrigin: 'center' }
    );
};

/** Preenche os segmentos de progresso: os anteriores já cheios, o atual cresce da esquerda. */
const atualizarProgresso = () => {
    const preenchimentos = etapasEl.value?.querySelectorAll('.tour-guiado__etapa-preenchimento');
    if (!preenchimentos?.length) return;

    preenchimentos.forEach((preenchimento, posicao) => {
        if (posicao < indice.value) {
            gsap.set(preenchimento, { scaleX: 1 });
        } else if (posicao === indice.value) {
            gsap.fromTo(
                preenchimento,
                { scaleX: 0 },
                { scaleX: 1, duration: dur(0.45), ease: 'power2.out' }
            );
        } else {
            gsap.set(preenchimento, { scaleX: 0 });
        }
    });
};

const irParaPasso = async (novoIndice, direcao = 1) => {
    if (novoIndice < 0 || novoIndice >= props.passos.length) {
        finalizar();
        return;
    }

    // Pula passos cujo alvo não existe na tela (campos condicionais, por exemplo).
    // No modo controlado quem garante o alvo é o pai, que espera ele aparecer
    if (!props.controlado && !passoDisponivel(props.passos[novoIndice])) {
        irParaPasso(novoIndice + direcao, direcao);
        return;
    }

    const deslocamento = direcao >= 0 ? -8 : 8;

    if (conteudoEl.value) {
        await gsap.to(conteudoEl.value, {
            opacity: 0,
            y: deslocamento,
            duration: dur(0.14),
            ease: 'power2.in',
        });
    }

    definirIndice(novoIndice);
    emit('mudar-passo', novoIndice);

    await nextTick();
    medirBalao();

    const alvo = elementoAlvo(passoAtual.value);
    if (alvo) garantirVisivel(alvo);

    iniciarPulso();
    atualizarProgresso();

    gsap.fromTo(
        conteudoEl.value,
        { opacity: 0, y: -deslocamento },
        { opacity: 1, y: 0, duration: dur(0.28), ease: 'power2.out' }
    );
};

const proximo = () => (props.controlado ? emit('proximo') : irParaPasso(indice.value + 1, 1));
const anterior = () => (props.controlado ? emit('anterior') : irParaPasso(indice.value - 1, -1));

/** Escurece/clareia o fundo tweenando a opacidade do box-shadow do recorte. */
const animarFundo = (de, para, duracao, aoConcluir) => {
    const estado = { valor: de };
    animacaoFundo?.kill();
    animacaoFundo = gsap.to(estado, {
        valor: para,
        duration: dur(duracao),
        ease: 'power2.out',
        onUpdate: () => destaqueEl.value?.style.setProperty('--opacidade-fundo', String(estado.valor)),
        onComplete: aoConcluir,
    });
};

const encerrar = (evento) => {
    if (!ativo.value) return;

    removerOuvintes();
    pararLoop();
    animacaoPulso?.kill();

    const concluir = () => {
        seguidor = null;
        ativo.value = false;
        oculto.value = false;
        focoAnterior?.focus?.({ preventScroll: true });
        focoAnterior = null;
        if (evento) emit(evento);
    };

    if (movimentoReduzido() || !cartaoEl.value || oculto.value) {
        concluir();
        return;
    }

    gsap.to(cartaoEl.value, { opacity: 0, scale: 0.96, duration: 0.2, ease: 'power2.in' });
    animarFundo(OPACIDADE_FUNDO, 0, 0.26, concluir);
};

const finalizar = () => encerrar('finalizar');
const pular = () => encerrar('pular');
/** Fecha sem emitir nada: usado pelo pai quando ele mesmo decide parar (ex.: tutorial pausado). */
const fechar = () => encerrar(null);

const aoTeclar = (evento) => {
    if (!ativo.value || oculto.value) return;
    if (evento.key === 'Escape') {
        pular();
        return;
    }

    // Enter e setas pertencem ao campo enquanto o usuário digita
    if (campoEditavel(evento.target)) return;

    if (evento.key === 'ArrowRight' || evento.key === 'Enter') {
        evento.preventDefault();
        proximo();
    } else if (evento.key === 'ArrowLeft') {
        evento.preventDefault();
        anterior();
    }
};

const adicionarOuvintes = () => {
    document.addEventListener('keydown', aoTeclar);
};

const removerOuvintes = () => {
    document.removeEventListener('keydown', aoTeclar);
};

const iniciar = async (indiceInicial = null) => {
    if (!props.passos.length || ativo.value) return;

    const primeiroValido = indiceInicial ?? props.passos.findIndex(passoDisponivel);
    if (primeiroValido === -1) return;

    focoAnterior = document.activeElement;
    ativo.value = true;
    oculto.value = false;
    definirIndice(primeiroValido);
    adicionarOuvintes();
    emit('iniciar');

    await nextTick();
    medirBalao();
    cartaoEl.value?.focus({ preventScroll: true });

    destaqueEl.value?.style.setProperty('--opacidade-fundo', '0');

    const alvo = elementoAlvo(passoAtual.value);
    if (alvo) garantirVisivel(alvo);

    posicionarInstantaneo();
    iniciarLoop();
    iniciarPulso();
    atualizarProgresso();

    animarFundo(0, OPACIDADE_FUNDO, 0.35);
    gsap.fromTo(
        cartaoEl.value,
        { opacity: 0, scale: 0.96, y: 10 },
        { opacity: 1, scale: 1, y: 0, duration: dur(0.35), ease: 'power3.out' }
    );
};

/** Some com balão e fundo sem encerrar o tour: o pai chama enquanto troca de tela ou espera o alvo. */
const ocultar = () => {
    if (!ativo.value || oculto.value) return;

    oculto.value = true;
    animacaoPulso?.kill();
    gsap.to(cartaoEl.value, { opacity: 0, duration: dur(0.15), ease: 'power2.in' });
    animarFundo(OPACIDADE_FUNDO, 0, 0.2);
};

/** Volta de `ocultar` já posicionado no novo alvo, sem atravessar a tela vindo da posição antiga. */
const reaparecer = async (novoIndice) => {
    oculto.value = false;
    definirIndice(novoIndice);
    emit('mudar-passo', novoIndice);

    await nextTick();
    medirBalao();

    const alvo = elementoAlvo(passoAtual.value);
    if (alvo) garantirVisivel(alvo);

    posicionarInstantaneo();
    iniciarPulso();
    atualizarProgresso();

    gsap.set(conteudoEl.value, { opacity: 1, y: 0 });
    animarFundo(0, OPACIDADE_FUNDO, 0.3);
    gsap.fromTo(
        cartaoEl.value,
        { opacity: 0, scale: 0.96, y: 10 },
        { opacity: 1, scale: 1, y: 0, duration: dur(0.3), ease: 'power3.out' }
    );
};

/** Ponto de entrada do modo controlado: inicia, reaparece ou transiciona conforme o estado atual. */
const mostrarPasso = (novoIndice, direcao = 1) => {
    if (!ativo.value) return iniciar(novoIndice);
    if (oculto.value) return reaparecer(novoIndice);
    return irParaPasso(novoIndice, direcao);
};

onBeforeUnmount(() => {
    removerOuvintes();
    pararLoop();
    animacaoPulso?.kill();
    animacaoFundo?.kill();
});

defineExpose({ iniciar, finalizar, pular, proximo, anterior, ativo, mostrarPasso, ocultar, fechar });
</script>

<template>
    <Teleport to="body">
        <div v-if="ativo && passoAtual" class="tour-guiado" :class="{ 'tour-guiado--oculto': oculto }">
            <div ref="destaqueEl" class="tour-guiado__destaque"
                :class="{ 'tour-guiado__destaque--passante': props.controlado }" aria-hidden="true" @click="pular">
                <div ref="pulsoEl" class="tour-guiado__pulso" :class="{ 'hidden': semAlvo }"></div>
            </div>

            <div ref="balaoEl" class="tour-guiado__balao">
                <section ref="cartaoEl" class="tour-guiado__cartao" :class="`tour-guiado__cartao--${lado}`" role="dialog"
                    aria-modal="false" :aria-labelledby="idTitulo" tabindex="-1">
                    <div class="tour-guiado__topo">
                        <div ref="etapasEl" class="tour-guiado__etapas" role="presentation">
                            <span v-for="posicao in props.passos.length" :key="posicao" class="tour-guiado__etapa">
                                <span class="tour-guiado__etapa-preenchimento"></span>
                            </span>
                        </div>

                        <button type="button" class="tour-guiado__fechar" aria-label="Sair do tutorial" @click="pular">
                            <i class="pi pi-times text-xs" aria-hidden="true"></i>
                        </button>
                    </div>

                    <div ref="conteudoEl" class="tour-guiado__miolo">
                        <span class="tour-guiado__rotulo" aria-live="polite">{{ rotuloPasso }}</span>

                        <h3 :id="idTitulo" class="tour-guiado__titulo">{{ titulo }}</h3>

                        <p class="tour-guiado__texto">{{ passoAtual.content }}</p>
                    </div>

                    <footer class="tour-guiado__rodape">
                        <button v-if="!primeiroPasso" type="button" class="tour-guiado__voltar" @click="anterior">
                            {{ props.rotulos.anterior }}
                        </button>

                        <button type="button" class="tour-guiado__avancar" @click="avancar">
                            {{ rotuloAcao }}
                            <i :class="iconeAcao" aria-hidden="true"></i>
                        </button>
                    </footer>
                </section>
            </div>
        </div>
    </Teleport>
</template>

<style scoped>
.tour-guiado {
    --tour-fundo: #ffffff;
    --tour-borda: rgba(0, 0, 0, 0.08);
    --tour-titulo: #111827;
    --tour-texto: #64748b;
    --tour-suave: rgba(0, 0, 0, 0.08);
    --tour-acento: #16a34a;
    --tour-acento-forte: #15803d;

    position: fixed;
    inset: 0;
    z-index: 2000;
    pointer-events: none;
}

.tour-guiado__destaque {
    --opacidade-fundo: 0;
    position: fixed;
    top: 0;
    left: 0;
    border-radius: 10px;
    pointer-events: auto;
    box-shadow: 0 0 0 9999px rgba(17, 24, 39, var(--opacidade-fundo));
    will-change: transform, width, height;
}

/* No modo controlado o tour só informa: clicar no destaque não encerra o tutorial */
.tour-guiado__destaque--passante,
.tour-guiado--oculto .tour-guiado__destaque,
.tour-guiado--oculto .tour-guiado__balao {
    pointer-events: none;
}

.tour-guiado__pulso {
    position: absolute;
    inset: -2px;
    border-radius: 12px;
    border: 2px solid var(--tour-acento);
    pointer-events: none;
}

.tour-guiado__balao {
    position: fixed;
    top: 0;
    left: 0;
    width: 340px;
    max-width: calc(100vw - 24px);
    pointer-events: auto;
    will-change: transform;
}

.tour-guiado__cartao {
    position: relative;
    padding: 1.25rem;
    border-radius: 1.25rem;
    background: var(--tour-fundo);
    border: 1px solid var(--tour-borda);
    box-shadow: 0 24px 60px -18px rgba(17, 24, 39, 0.35);
}

.tour-guiado__cartao:focus {
    outline: none;
}

/* Seta: quadrado rotacionado herdando o fundo do cartão, sem z-index negativo
   (o GSAP cria contexto de empilhamento ao animar o cartão). */
.tour-guiado__cartao::after {
    content: '';
    position: absolute;
    width: 14px;
    height: 14px;
    background: var(--tour-fundo);
    transform: rotate(45deg);
}

/* A seta recebe borda apenas nos dois lados que ficam para fora do cartão,
   para acompanhar a borda do balão sem cortar o fundo. */
.tour-guiado__cartao--bottom::after {
    top: -6px;
    left: var(--seta-offset);
    margin-left: -7px;
    border-top: 1px solid var(--tour-borda);
    border-left: 1px solid var(--tour-borda);
}

.tour-guiado__cartao--top::after {
    bottom: -6px;
    left: var(--seta-offset);
    margin-left: -7px;
    border-bottom: 1px solid var(--tour-borda);
    border-right: 1px solid var(--tour-borda);
}

.tour-guiado__cartao--right::after {
    left: -6px;
    top: var(--seta-offset);
    margin-top: -7px;
    border-bottom: 1px solid var(--tour-borda);
    border-left: 1px solid var(--tour-borda);
}

.tour-guiado__cartao--left::after {
    right: -6px;
    top: var(--seta-offset);
    margin-top: -7px;
    border-top: 1px solid var(--tour-borda);
    border-right: 1px solid var(--tour-borda);
}

.tour-guiado__cartao--centro::after {
    display: none;
}

.tour-guiado__topo {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.tour-guiado__etapas {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    flex: 1;
    min-width: 0;
}

.tour-guiado__etapa {
    position: relative;
    flex: 1;
    height: 3px;
    border-radius: 999px;
    background: var(--tour-suave);
    overflow: hidden;
}

.tour-guiado__etapa-preenchimento {
    position: absolute;
    inset: 0;
    border-radius: 999px;
    background: var(--tour-acento);
    transform: scaleX(0);
    transform-origin: left center;
}

.tour-guiado__fechar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 1.75rem;
    height: 1.75rem;
    border: none;
    border-radius: 50%;
    background: var(--tour-suave);
    color: var(--tour-titulo);
    font-size: 0.7rem;
    cursor: pointer;
    transition: background-color 0.15s ease;
}

.tour-guiado__fechar:hover {
    background: rgba(0, 0, 0, 0.16);
}

.tour-guiado__miolo {
    margin-top: 1.5rem;
}

.tour-guiado__rotulo {
    display: block;
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--tour-texto);
}

.tour-guiado__titulo {
    margin: 0.625rem 0 0;
    font-size: 1.1rem;
    font-weight: 600;
    line-height: 1.35;
    color: var(--tour-titulo);
}

.tour-guiado__texto {
    margin: 0.5rem 0 0;
    max-height: 40vh;
    overflow-y: auto;
    overscroll-behavior: contain;
    white-space: pre-line;
    font-size: 0.8rem;
    line-height: 1.55;
    color: var(--tour-texto);
}

.tour-guiado__rodape {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 2rem;
}

.tour-guiado__voltar {
    border: none;
    background: none;
    padding: 0.5rem 0.25rem;
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--tour-titulo);
    cursor: pointer;
    transition: color 0.15s ease;
}

.tour-guiado__voltar:hover {
    color: var(--tour-texto);
}

.tour-guiado__avancar {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    border: none;
    border-radius: 999px;
    padding: 0.625rem 1.5rem;
    background: var(--tour-acento);
    color: #ffffff;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.15s ease, transform 0.15s ease;
}

.tour-guiado__avancar:hover {
    background: var(--tour-acento-forte);
}

.tour-guiado__avancar:active {
    transform: scale(0.97);
}

.tour-guiado__avancar i {
    font-size: 0.75rem;
}
</style>
